import { IPROP_MAP, modifyProps } from '../../generators/modifyProps';
import { getRequiredLabel, useInputState } from '../jdsinputfield/logic';
import { useEffect, useState } from 'react';

import { JDSSliderProps } from './generated';

export interface SliderProps
  extends Omit<
    JDSSliderProps,
    | 'onValueChange'
    | 'onFocus'
    | 'onBlur'
    | 'onChange'
    | 'onSubmit'
    | 'onKeydown'
    | 'labelMin'
    | 'labelMax'
  > {
  id?: string;
  /**
   * @deprecated
   * use `stateText` instead
   */
  stateConfig?: {
    errorText?: string;
    successText?: string;
    warningText?: string;
    requiredText?: string;
  };
  onClick?: (ev: React.MouseEvent<HTMLInputElement>) => void;
  onFocus?: (ev: React.FocusEvent<HTMLInputElement>) => void;
  onBlur?: (ev: React.FocusEvent<HTMLInputElement>) => void;
  onChange?: (ev: React.ChangeEvent<HTMLInputElement>) => void;
  onKeydown?: () => void;
  onInvalid?: (ev: React.InvalidEvent<HTMLInputElement>) => void;
  required?: boolean;
  ariaLabel?: string;
  ariaDescribedby?: string;
}

export const PROP_MAP: IPROP_MAP<SliderProps> = (props) => ({
  onChange: {
    properties: {
      onChange: props.onChange,
      onValueChange: props.onChange
    }
  }
});

export function getSteppedValue(
  value: undefined | number,
  step = 1,
  min = 0,
  max = 100
): number {
  if (typeof value !== 'number') {
    return min;
  }
  step = step > 0 ? step : 1;
  const clamped = clamp(value, min, max);
  return Math.round((clamped - min) / step) * step + min;
}

const clamp = (value: number, min: number, max: number) =>
  Math.max(min, Math.min(value, max));

export const withLogic =
  (Component: React.ComponentType<SliderProps>): React.FC<SliderProps> =>
  (userProps: SliderProps) => {
    const props = modifyProps(userProps, PROP_MAP);
    const { value: userValue = 0 } = props;
    const { inputState, onInvalid, stateText } = useInputState(
      props.state,
      props.required,
      props.onInvalid,
      props.stateConfig
    );

    const [value, setValue] = useState(userValue);
    const [inputValue, setInputValue] = useState(userValue);

    useEffect(() => {
      setValue(userValue);
      setInputValue(userValue);
    }, [userValue]);

    const getSuffixValue = (value: number, editable?: boolean) => {
      if (editable) {
        return value;
      }
      return Intl.NumberFormat().format(value);
    };

    return (
      <Component
        {...{
          ...props,
          value,
          valueSuffix: getSuffixValue(inputValue, props.editableTextBox),
          state: inputState,
          stateText: stateText || props.stateText,
          onInvalid,
          label: getRequiredLabel(props.label, props.required),
          max: props.max || 100,
          min: props.min || 0,
          onBlur: (ev: React.FocusEvent<HTMLInputElement>) => {
            const currentValue = ev.target.value
              ? parseInt(ev.target.value)
              : value;
            const steppedValue = getSteppedValue(
              currentValue,
              props.step,
              props.min,
              props.max
            );
            setValue(steppedValue);
            const tracker = Object.getOwnPropertyDescriptor(
              window.HTMLInputElement.prototype,
              'value'
            )?.set;
            tracker?.call(ev.target, currentValue);

            setInputValue(steppedValue);
            props?.onChange?.({
              ...ev,
              target: { ...ev.target, value: steppedValue }
            });
          },
          onChange: (ev: React.ChangeEvent<HTMLInputElement>) => {
            setInputValue(parseInt(ev.target.value));
          },
          onValueChange: (ev: React.ChangeEvent<HTMLInputElement>) => {
            setValue(parseInt(ev.target.value));
            setInputValue(ev.target.value);
            props?.onChange?.(ev);
          },
          labelMin: Intl.NumberFormat().format(props.min || 0),
          labelMax: Intl.NumberFormat().format(props.max || 100)
        }}
      />
    );
  };
